import type { ComponentChildren } from 'preact'
import styles from './SettingsGroup.module.css'

/**
 * One collapsible area of Settings (D-099).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  SETTINGS WAS A JUNK DRAWER, AND THE FIX IS STRUCTURE RATHER THAN REMOVAL
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Before C6.3 this screen was ten to twelve `<h2>` sections on one scroll,
 *  spanning identity editing, project lifecycle, phase lifecycle, backup, restore,
 *  storage diagnostics, app version, branding, selective deletion and a full
 *  reset. Nothing was wrong with any one of them; the problem was that finding any
 *  of them meant reading all of them.
 *
 *  Six groups, and **every one collapsed by default**. That is the deliberate
 *  choice: a screen of six headings can be scanned in a second, and the thing the
 *  user came for is one tap away rather than one hunt away. It also makes the
 *  screen's height independent of how much the app can do, which is what stopped
 *  it growing every checkpoint.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  A NATIVE `<details>`, AND NOT A HAND-ROLLED DISCLOSURE
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  `<details>`/`<summary>` is keyboard-operable, announced correctly as expanded
 *  or collapsed, findable by the browser's own in-page search even while closed,
 *  and needs no JavaScript. A bespoke button-and-panel would need `aria-expanded`,
 *  `aria-controls`, its own key handling and its own focus management, and would
 *  be the fourth place in this app to reimplement something the platform does —
 *  the same reasoning that kept the photo comparison on a native `range`.
 *
 *  The marker is hidden and replaced with text, because a rotating triangle is a
 *  state carried by a glyph alone.
 */
export interface SettingsGroupProps {
  readonly id: string
  readonly title: string
  /** One line saying what is inside, so the heading need not be guessed at. */
  readonly summary: string
  /**
   * Open on first render.
   *
   * Used for a group holding something that needs attention now — a restore
   * awaiting confirmation. It is not a preference and is not remembered: a group
   * that stayed open because it was opened once would undo the point of the
   * collapse.
   */
  readonly defaultOpen?: boolean
  readonly children: ComponentChildren
}

export function SettingsGroup({
  id,
  title,
  summary,
  defaultOpen = false,
  children,
}: SettingsGroupProps) {
  return (
    <details class={styles.group} open={defaultOpen}>
      <summary class={styles.summary}>
        {/* An `h2` inside the summary keeps the heading structure the screen had
            before, so a screen-reader user can still navigate Settings by heading
            rather than having to open every group to find out what is in it. */}
        <h2 id={id} class={styles.title}>
          {title}
        </h2>
        <span class={styles.hint}>{summary}</span>
      </summary>
      <div class={styles.body}>{children}</div>
    </details>
  )
}
